/**
 * The worked examples a learner is shown before the real thing arrives. F-141, step 3.
 *
 * *"you will get briefings — they look like this, and they are knowledge. Then questions — they
 * look like this."* The learner has never seen either shape, and the product's whole value is
 * delivered through the two of them: showing them in the minutes before the real ones start is the
 * difference between a first briefing that reads as an odd wall of text and one that reads as the
 * thing they were promised.
 *
 * ---
 *
 * **Hybrid, which is the owner's answer and not a compromise.** Asked whether these should be fixed
 * copy or real model output, Leo chose *"fixed templates with a few variables that makes them feel
 * real to their objective"*. So:
 *
 * - **The shapes and the words are fixed.** They live in `i18n.ts` in both languages, they are
 *   always correct, they cost nothing and they cannot hallucinate. A model call here would be a
 *   paid, slow, fallible way to produce a sentence whose job is to be recognisable.
 * - **The learner's own subject titles fill the slots.** A sample briefing about *their* nervous
 *   system rather than about a stranger's. That is the whole of the personalisation and it is why
 *   this module exists rather than the copy being rendered directly.
 *
 * ---
 *
 * **These are illustrations, not messages, and that is a hard constraint rather than a
 * presentational one.**
 *
 * - **They are never written to `MSG#`.** Nothing in this module or its callers posts anything: a
 *   sample in the message log is in the learner's transcript for ever *and* in the operator's
 *   timeline the day somebody reports a problem, and the operator has no way to tell it from a
 *   briefing the sweep actually sent.
 * - **They are not answerable.** There is no item behind the sample question, so an answer to it
 *   would be graded against nothing and enter no review state — which the design names as *a worse
 *   first experience than no sample at all*. The renderer says so in words and offers no way to
 *   reply to one.
 * - **They are marked.** Every sample carries its own label and sits in a block that says what it
 *   is; see `Samples.tsx`.
 *
 * DOM-free, so `packages/web/test/onboarding-samples.test.ts` can assert the slot-filling from
 * plain Node — the one part of this with a way to be wrong.
 */
/* `.js`, like `nav.ts` and `chat/suggest.ts`: this module is compiled for plain Node as well as
   for the bundler, and Node's ESM resolver does not guess extensions. Both imports here are
   `import type` and are erased, so today it would work either way — which is exactly why the
   extension goes on now, rather than on the day somebody adds the first value import and finds
   `npm test` failing with `ERR_MODULE_NOT_FOUND` in a file they did not change. */
import type { CopyKey } from '../i18n.js';
import type { BubbleKind } from '../chat/kinds.js';

/**
 * How long a subject title may be before it stops being a slot and starts being the sample.
 *
 * The outline writes these and they are ordinarily a few words, but nothing enforces that and the
 * template reads as a sentence with a name in it. A title longer than this is cut rather than
 * allowed to push the shape out of recognisability — the sample's job is the shape.
 */
export const SLOT_MAX = 72;

/** The two shapes, in the order the learner will meet them. */
export interface SampleShape {
  /**
   * Which bubble this is drawn as.
   *
   * Deliberately the chat's own `BubbleKind` and not a look of its own: a sample that did not
   * resemble the real message would be teaching the learner the wrong shape, which is the only way
   * this feature can fail at its actual job. What separates it from a real message is the label and
   * the block around it, never the bubble.
   */
  readonly kind: BubbleKind;
  /**
   * The fixed template. Both locales, enforced by `i18n.ts`'s types.
   *
   * **The template names its own slot**, rather than this table saying which one it gets. The two
   * arrangements look equivalent and are not: a slot named here and a slot written in the copy are
   * two facts that have to agree, and the copy is the half that gets edited — by somebody fixing a
   * translation, in a file with two thousand strings in it and no view of this table. Naming it
   * once, in the sentence it appears in, is the arrangement that cannot drift.
   */
  readonly body: CopyKey;
}

/**
 * Briefing then question, because that is the order they arrive in and the order the sentence
 * above says them in. Two and not five: this is shown once, to somebody waiting, and a tour that
 * scrolls is a tour nobody finishes.
 */
export const SAMPLES: readonly SampleShape[] = [
  { kind: 'briefing', body: 'tour.sample.briefing' },
  { kind: 'question', body: 'tour.sample.question' },
];

/** What fills the slots: one title for the briefing, ideally a different one for the question. */
export interface SampleSlots {
  /** `{subject}` in the copy. The first thing the outline said this objective is about. */
  subject: string;
  /**
   * `{second}` in the copy: a second subject, so the two samples are not about the same thing.
   *
   * Falls back to the first, and then to the objective, and then to fixed copy. A learner whose
   * outline has not landed yet still gets a sample — it is simply about their objective rather
   * than about one of its subjects, which is still theirs and still true.
   */
  second: string;
}

/**
 * A title reduced to something that can sit inside a sentence, or nothing.
 *
 * Cut by **characters and not by code units**: `String.prototype.slice` counts UTF-16 units, so a
 * title whose seventy-second character is an emoji or an astral glyph would be halved into a lone
 * surrogate and render as `�` immediately before the ellipsis. `Array.from` iterates code points,
 * which is what "seventy-two characters" means to the person who wrote the title.
 */
function usable(value: string | undefined): string | undefined {
  const trimmed = (value ?? '').replace(/\s+/g, ' ').trim();
  if (!trimmed) return undefined;
  const points = Array.from(trimmed);
  if (points.length <= SLOT_MAX) return trimmed;
  return `${points.slice(0, SLOT_MAX - 1).join('').trimEnd()}…`;
}

/**
 * What to put in the slots, from what the app happens to know at this moment.
 *
 * **Everything here is optional on purpose.** The outline takes about forty seconds and the tour
 * can be reached before it lands, so the honest inputs are "some titles, maybe none" and "an
 * objective title, maybe none". The fallback is fixed copy the caller passes in — already
 * localised, because this module has no `t`.
 *
 * Duplicates are dropped before `second` is chosen, so an outline that named the same subject twice
 * does not produce two samples about one thing and look like a bug.
 */
export function sampleSlots(
  titles: readonly (string | undefined)[],
  objective: string | undefined,
  fallback: string,
): SampleSlots {
  const seen: string[] = [];
  for (const title of titles) {
    const clean = usable(title);
    if (clean && !seen.includes(clean)) seen.push(clean);
  }
  const subject = seen[0] ?? usable(objective) ?? fallback;
  const second = seen[1] ?? subject;
  return { subject, second };
}

/**
 * The template with its slots filled.
 *
 * **One pass, and that is the property worth having.** A regular expression over the template
 * substitutes each slot once, so a subject title that itself contains `{subject}` — a learner may
 * type anything into an objective, and the outline writes from it — is inserted as characters and
 * never expanded again. The alternative, chained `String.replace` calls, would expand it on the
 * next call and turn a title into a template.
 *
 * A slot with no value is left standing rather than blanked. That is deliberately visible: a
 * sample reading "a briefing about {subject}" is obviously broken and gets fixed, where a sample
 * reading "a briefing about " looks like bad copy and survives for months.
 *
 * `Object.hasOwn` and not `in`, which walks the prototype chain: `{constructor}` in a template
 * would otherwise render the source of a function. Not reachable today — the templates are fixed
 * copy in `i18n.ts` — but it costs nothing to close, and "not reachable today" is the sentence
 * that precedes most of the entries in this repository's fault list.
 */
export function fill(template: string, slots: SampleSlots): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.hasOwn(slots, name) ? slots[name as keyof SampleSlots] : whole,
  );
}
